import type { ReactNode } from "react";
import {
  BookOpen,
  BotMessageSquare,
  Inbox,
  Table2,
  Workflow,
  type LucideIcon,
} from "lucide-react";

export type BlockType =
  | "flow"
  | "assistant"
  | "knowledge_base"
  | "table"
  | "inbox";

export const TYPE_CONFIG: Record<
  BlockType,
  {
    icon: LucideIcon;
    existingTab: "flows" | "co-workers" | "knowledge-bases" | "tables";
    href: (id: string) => string;
    // Its list on the home page, where a deleted one with no open sibling lands.
    listHref: string;
  }
> = {
  flow: {
    icon: Workflow,
    existingTab: "flows",
    href: (id) => `/flows/${id}`,
    listHref: "/?tab=flows",
  },
  assistant: {
    icon: BotMessageSquare,
    existingTab: "co-workers",
    href: (id) => `/assistant/${id}`,
    listHref: "/?tab=agents",
  },
  knowledge_base: {
    icon: BookOpen,
    existingTab: "knowledge-bases",
    href: (id) => `/data-hub/${id}`,
    listHref: "/?tab=knowledge-bases",
  },
  table: {
    icon: Table2,
    existingTab: "tables",
    href: (id) => `/tables/${id}`,
    listHref: "/?tab=tables",
  },
  inbox: {
    icon: Inbox,
    existingTab: "flows",
    href: (id) => `/inbox/${id}`,
    listHref: "/",
  },
};

// The kinds that open in a tool page (ToolLayout); an inbox has its own.
export const TOOL_TYPES = [
  "flow",
  "assistant",
  "knowledge_base",
  "table",
] as const satisfies readonly BlockType[];
export type ToolType = (typeof TOOL_TYPES)[number];

// The tool a path is on, if any: its page is TYPE_CONFIG's href.
export const toolAt = (
  pathname: string
): { type: ToolType; id: string } | null => {
  for (const type of TOOL_TYPES) {
    const prefix = TYPE_CONFIG[type].href("");
    const id = pathname.startsWith(prefix)
      ? pathname.slice(prefix.length).match(/^[a-f0-9-]+/)?.[0]
      : undefined;
    if (id) return { type, id };
  }
  return null;
};

// Something a tool can do from its menu (its header name, its open-tools row).
export type BlockAction = {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  // Either the "destructive" token or a CSS colour.
  color?: string;
  auth?: {
    level: "tenant" | "group";
    permissions: string[];
    requiresAllPermissions?: boolean;
  };
};

export const isDestructive = (color: string | undefined) =>
  color === "destructive" || color === "var(--destructive)";

type HasPermissions = (
  permissions: string[],
  level: "tenant" | "group",
  mode: "all" | "any"
) => boolean;

export const allowedActions = (
  actions: BlockAction[],
  hasPermissions: HasPermissions
) =>
  actions.filter((action) =>
    action.auth
      ? hasPermissions(
          action.auth.permissions,
          action.auth.level,
          action.auth.requiresAllPermissions ? "all" : "any"
        )
      : true
  );

// Where an open tool lives: where it was opened from, or else its own page. A
// stored href is followed only when it is a path on this site (browsers read
// a backslash as a slash, so "/\host" is "//host", another site).
export const blockHref = (block: {
  type: BlockType;
  id: string;
  href?: string;
}) =>
  block.href?.startsWith("/") &&
  !block.href.startsWith("//") &&
  !block.href.includes("\\")
    ? block.href
    : TYPE_CONFIG[block.type].href(block.id);
